import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import ts from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import js from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import py from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import html from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import cLang from 'react-syntax-highlighter/dist/esm/languages/prism/c';
import cpp from 'react-syntax-highlighter/dist/esm/languages/prism/cpp';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import ruby from 'react-syntax-highlighter/dist/esm/languages/prism/ruby';
import diff from 'react-syntax-highlighter/dist/esm/languages/prism/diff';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/* ── Register languages ────────────────────────────────────────────── */
SyntaxHighlighter.registerLanguage('typescript', ts);
SyntaxHighlighter.registerLanguage('javascript', js);
SyntaxHighlighter.registerLanguage('python', py);
SyntaxHighlighter.registerLanguage('html', html);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('c', cLang);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('diff', diff);
SyntaxHighlighter.registerLanguage('markdown', markdown);

/* ── Aliases: LLMs use many variations of language names ────────────── */
const LANGUAGE_ALIASES: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  yml: 'yaml',
  md: 'markdown',
  cs: 'csharp',
  'c#': 'csharp',
  'c++': 'cpp',
  htm: 'html',
  xml: 'html',
  svg: 'html',
  plaintext: 'text',
  plain: 'text',
  txt: 'text',
  text: 'text',
  golang: 'go',
  mysql: 'sql',
  postgresql: 'sql',
  postgres: 'sql',
  sqlite: 'sql',
  jsonc: 'json',
  json5: 'json',
};

const SUPPORTED_LANGUAGES = new Set([
  'typescript', 'javascript', 'python', 'html', 'css', 'json', 'bash',
  'java', 'sql', 'yaml', 'c', 'cpp', 'csharp', 'go', 'ruby', 'diff', 'markdown',
]);

interface CodeBlockProps {
    language: string;
    children: string;
}

export default function CodeBlock({ language, children }: CodeBlockProps) {
    const resolved = LANGUAGE_ALIASES[language?.toLowerCase()] || language?.toLowerCase() || 'text';
    const isSupported = SUPPORTED_LANGUAGES.has(resolved);

    // Fallback for unsupported / unknown languages
    if (!isSupported) {
        return (
            <div
                style={{
                    backgroundColor: '#282c34',
                    color: '#abb2bf',
                    padding: '16px',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    overflow: 'auto',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    fontSize: '0.875em',
                    lineHeight: 1.6,
                }}
            >
                {language && language !== 'text' && (
                    <div style={{ color: '#636d83', fontSize: '0.75em', marginBottom: '8px', textTransform: 'uppercase' }}>
                        {language}
                    </div>
                )}
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <code>{children}</code>
                </pre>
            </div>
        );
    }

    return (
        <SyntaxHighlighter
            style={oneDark as any}
            language={resolved}
            PreTag="div"
            customStyle={{ borderRadius: '0.5rem', marginBottom: '1rem' }}
        >
            {children}
        </SyntaxHighlighter>
    );
}
